import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { ClientAccountTypeEnum } from "@/__generated__/graphql";
import { useParams } from "@/components/hooks/use-params";
import { Divider } from "@/components/shared/divider";
import { FormControlError } from "@/components/shared/form-control-error";
import { Grid } from "@/components/shared/grid";
import { HorizontalScroll } from "@/components/shared/horizontal-scroll";
import { IconLabel } from "@/components/shared/icon-label";
import { InputFormControl } from "@/components/shared/input-form-control";
import { Message } from "@/components/shared/message";
import { useRoute } from "@/components/shared/router";
import { TextareaFormControl } from "@/components/shared/textarea-form-control";
import { Tooltip } from "@/components/shared/tooltip";
import { Typography } from "@/components/shared/typography";
import { Button, Image, Input, View } from "@/components/ui";
import type { GiftCardGroup } from "@/containers/profile/account-gift-card/account-gift-card-history-component/lib";
import {
  getGiftCardOccasionGroup,
  getOccasionsByGroup,
} from "@/containers/profile/account-gift-card/account-gift-card-history-component/lib";
import { GiftCardGroupToggle } from "@/containers/profile/account-gift-card/gift-card-group-toggle";
import { cn, getPlatform } from "@/lib/utils";
import { useAuthState } from "@/store/auth";

import type { GiftCardFormData } from "./use-gift-card-form";

const amountOptions: { label: number; value: number }[] = [
  { label: 10, value: 10 },
  { label: 20, value: 20 },
  { label: 30, value: 30 },
  { label: 50, value: 50 },
  { label: 80, value: 80 },
  { label: 100, value: 100 },
];

export function SectionGiftCardForm() {
  const { control, getValues } = useFormContext<GiftCardFormData>();
  const { replace } = useRoute();
  const { key } = useParams<{ key?: string }>();
  const accountType = useAuthState((state) => state.data.userInfo.accountType);
  const isCorporate = accountType === ClientAccountTypeEnum.Corporate;

  const [group, setGroup] = useState<GiftCardGroup>(() =>
    getGiftCardOccasionGroup(key ?? String(getValues("occasion") ?? "")),
  );
  const occasions = getOccasionsByGroup(group);

  return (
    <>
      <Controller
        name="occasion"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <View className="py-3">
            <View className="p-4">
              <Typography variant="label-md">Pick a gift card</Typography>
            </View>
            <View className="px-4 pb-3">
              <GiftCardGroupToggle value={group} onChange={setGroup} />
            </View>
            <HorizontalScroll
              data={occasions}
              snap="start"
              contentPadding={{ start: 16, end: 16 }}
              itemWidth={82}
              itemSpacing={12}
              renderItem={({ title, key, icon }) => {
                const isSelected = field.value === key;
                return (
                  <IconLabel
                    icon={
                      <View
                        className={cn(
                          "bg-greyBlue-100 flex h-14 w-14 items-center justify-center rounded-lg",
                          isSelected && "border-primary-border bg-primary-surface border",
                          !isSelected && !!error ? "border-destructive border" : "",
                        )}
                      >
                        <View className="h-10 w-10">
                          <Image src={icon} className="h-full w-full" />
                        </View>
                      </View>
                    }
                    label={title}
                    className="cursor-pointer"
                    onClick={() => {
                      field.onChange(key);
                      if (getPlatform() === "web") {
                        replace({ pageKey: "giftCardNew", query: { key } });
                      }
                    }}
                  />
                );
              }}
            />
            <View className="px-4">
              <FormControlError error={error?.message} />
            </View>
          </View>
        )}
      />

      <Divider />

      <View className="flex flex-col gap-1 p-4 md:gap-3">
        <InputFormControl
          name="yourName"
          label={isCorporate ? "Sender Name" : "Your Name"}
          control={control}
          component={Input}
          componentProps={{
            placeholder: isCorporate ? "Enter sender name" : "Enter your name",
          }}
        />

        <InputFormControl
          name="title"
          label="Title"
          control={control}
          component={Input}
          componentProps={{ placeholder: "e.g., A Gift for You" }}
        />

        <TextareaFormControl
          name="message"
          label="Message"
          control={control}
          placeholder="Just a little something for you. Hope you enjoy it!"
          maxLength={200}
          showCharCount
        />

        {isCorporate && (
          <InputFormControl
            name="quantity"
            label="Number of Gifts (min 1, max 100)"
            control={control}
            component={Input}
            componentProps={{
              placeholder: "1",
              keyboardType: "numeric",
              type: "number",
            }}
          />
        )}

        {/* Amount */}
        <Controller
          name="amount"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <View className="pb-3">
              <Typography variant="label-sm" color="foreground-default" className="mb-1">
                Amount
              </Typography>
              <Grid
                columns={3}
                gap={12}
                data={amountOptions}
                render={({ label: optionLabel, value: optionValue }) => {
                  const isSelected = field.value === optionValue;
                  return (
                    <Button
                      variant="secondary"
                      color={isSelected ? "CTA2" : "foreground"}
                      className={
                        isSelected ? "bg-primary-surface hover:bg-primary-surface" : undefined
                      }
                      size="sm"
                      fullWidth="full"
                      onClick={() => {
                        field.onChange(isSelected ? 0 : optionValue);
                      }}
                    >
                      {`$${optionLabel}`}
                    </Button>
                  );
                }}
              />
              <FormControlError error={error?.message} />
            </View>
          )}
        />

        <InputFormControl
          name="amount"
          label="Custom"
          control={control}
          component={Input}
          componentProps={{
            placeholder: "Enter amount",
            keyboardType: "numeric",
          }}
        />

        {isCorporate ? (
          <View>
            <View className="flex flex-row items-center gap-1 pb-1">
              <Typography variant="label-sm" color="foreground-default">
                Validity Before Redemption
              </Typography>
              <Tooltip content="Corporate accounts can set the gift card validity period before redemption (in months). After redemption, the credit is valid for 12 months." />
            </View>
            <InputFormControl
              name="expirationPeriod"
              control={control}
              component={Input}
              componentProps={{
                placeholder: "Enter number of months",
                keyboardType: "numeric",
                type: "number",
              }}
            />
          </View>
        ) : (
          <Message
            message="Gift cards are valid for 12 months after purchase. Once redeemed, the balance will become account credit valid for another 12 months."
            variant="info"
          />
        )}
      </View>
    </>
  );
}
